import { ReactNode, useEffect, useRef, useState } from "react";
import lottie, { AnimationItem } from "lottie-web";
import { cloneLottie, loadTgs } from "../lib/tgsCache";

/**
 * One pack sticker. It only fetches/renders while it is on (or near) the screen and
 * releases its player when it scrolls away, so even a 200-sticker pack stays light.
 */
export function PackLottie({
  id,
  size,
  animate = true,
  fallback,
}: {
  id: string;
  size: number;
  animate?: boolean;
  fallback?: ReactNode;
}) {
  const host = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver((entries) => setVisible(entries.some((e) => e.isIntersecting)), {
      rootMargin: "240px",
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const el = host.current;
    if (!visible || !el) return;
    let cancelled = false;
    let anim: AnimationItem | null = null;
    setFailed(false);
    loadTgs(id)
      .then((json) => {
        if (cancelled || !host.current) return;
        anim = lottie.loadAnimation({
          container: host.current,
          renderer: "svg",
          loop: animate,
          autoplay: animate,
          animationData: cloneLottie(json),
          rendererSettings: { preserveAspectRatio: "xMidYMid meet", progressiveLoad: true },
        });
        if (!animate) anim.goToAndStop(0, true);
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      setReady(false);
      anim?.destroy();
    };
  }, [visible, id, animate]);

  return (
    <div ref={host} className="relative shrink-0" style={{ width: size, height: size }}>
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center">
          {failed ? (
            fallback ?? <span className="text-tg-hint text-xs">—</span>
          ) : (
            <div className="w-1/2 h-1/2 rounded-full bg-black/20 animate-pulse" />
          )}
        </div>
      )}
    </div>
  );
}
